import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { Hosts } from '#api';
import { enrollmentCodeQueryKeys } from '#api/query-keys.ts';
import { useTimeout } from './useTimeout.ts';

const makeEnrollmentTokenQueryOptions = (hostId: string) =>
  queryOptions({
    queryKey: enrollmentCodeQueryKeys.forHost(hostId),
    queryFn: ({ signal }) => Hosts.getEnrollmentToken(hostId, { signal }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

/**
 * {@link useEnrollmentToken} fetches an enrollment code for a host.  This code is valid for a certain amount of time,
 * after which it is considered stale, and refocusing the window or reconnecting to the internet
 * will cause a new code to be fetched.
 */
export function useEnrollmentToken(hostId: string, { enabled }: { enabled: boolean }) {
  const { data, refetch, isFetching } = useEnrollmentTokenQuery(hostId, enabled);
  const queryClient = useQueryClient();

  const removeTokenCache = useCallback(() => {
    queryClient.removeQueries(makeEnrollmentTokenQueryOptions(hostId));
  }, [hostId, queryClient]);

  const { lifetimeSeconds, code: token } = data ?? {};
  const [isExpired, setIsExpired] = useState<boolean | undefined>(undefined);
  const { start, isStarted } = useTimeout(() => setIsExpired(true), lifetimeSeconds && lifetimeSeconds * 1000);

  // Start the expiration timeout if it's not already running and we have a time to use
  useEffect(() => {
    if (lifetimeSeconds && !isStarted) {
      start();
    }
  }, [lifetimeSeconds, start, isStarted]);

  return { token, isExpired, refetchToken: refetch, isFetching, removeTokenCache };
}

function useEnrollmentTokenQuery(hostId: string, enabled: boolean) {
  return useQuery({
    ...makeEnrollmentTokenQueryOptions(hostId),
    enabled,
  });
}
